import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { GoogleLogo, PlugsConnected, EnvelopeSimple, Clock, PaperPlaneTilt, CheckCircle, Warning, ArrowLeft, ArrowRight, Plus, Trash } from "@phosphor-icons/react";

const STEPS = [
  { id: 1, label: "Connect inbox" },
  { id: 2, label: "Pick a template" },
  { id: 3, label: "Personalize" },
  { id: 4, label: "Schedule" },
  { id: 5, label: "Review & activate" },
];

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function EmailSetup() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [connection, setConnection] = useState(null);
  const [connLoading, setConnLoading] = useState(true);

  const [starterTemplates, setStarterTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [sequence, setSequence] = useState({
    name: "",
    description: "",
    active: true,
    auto_enroll_new_contacts: true,
    sending: {
      window_start_hour: 9,
      window_end_hour: 17,
      weekdays: [0, 1, 2, 3, 4],
      tz_offset_hours: -new Date().getTimezoneOffset() / 60,
    },
    steps: [],
  });

  const [testEmail, setTestEmail] = useState("");
  const [testSubject, setTestSubject] = useState("Testing my Nexus sequence — {{contact.first_name}}");
  const [testBody, setTestBody] = useState("Hi {{contact.first_name}}, this is a test send from MyNexusCard.\n\n— {{me.first_name}}");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c1, c2, tpl] = await Promise.all([
          api.get("/email/config"),
          api.get("/email/connection"),
          api.get("/email/starter-templates"),
        ]);
        setConfig(c1.data);
        setConnection(c2.data.connection);
        setStarterTemplates(tpl.data.templates || []);
      } finally {
        setConnLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    // Handle OAuth callback query params.
    if (params.get("connected") === "1") {
      toast.success("Gmail connected");
      setParams({}, { replace: true });
      api.get("/email/connection").then((r) => setConnection(r.data.connection));
    }
    if (params.get("connect_error")) {
      toast.error(`Connection failed: ${params.get("connect_error")}`);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const isConnected = useMemo(() => {
    if (!connection) return false;
    return connection.status === "active";
  }, [connection]);

  const isDevMode = config?.mode === "dev" && !config?.branded_sender;

  const connectGoogle = async () => {
    try {
      const r = await api.get("/email/connect/google");
      if (r.data.mode === "dev") {
        toast.message("Demo Mode active", { description: r.data.message });
        setConnection((await api.get("/email/connection")).data.connection);
        return;
      }
      if (r.data.auth_url) {
        window.location.href = r.data.auth_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start OAuth");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this inbox? Active sequences will be paused until you reconnect.")) return;
    try {
      await api.post("/email/disconnect");
      toast.success("Disconnected");
      setConnection(null);
    } catch (e) {
      toast.error("Failed to disconnect");
    }
  };

  const pickTemplate = (t) => {
    setSelectedTemplateId(t.id);
    setSequence((s) => ({
      ...s,
      name: t.name,
      description: t.description,
      sending: { ...s.sending, ...t.sending },
      steps: t.steps.map((step, i) => ({
        order: step.order ?? i,
        delay_days: step.delay_days,
        subject: step.subject,
        body: step.body,
        active: true,
      })),
    }));
  };

  const updateStep = (idx, patch) => {
    setSequence((s) => ({ ...s, steps: s.steps.map((st, i) => (i === idx ? { ...st, ...patch } : st)) }));
  };
  const addStep = () => {
    setSequence((s) => ({
      ...s,
      steps: [
        ...s.steps,
        { order: s.steps.length, delay_days: 3, subject: "Follow up", body: "Hi {{contact.first_name}},\n\n", active: true },
      ],
    }));
  };
  const removeStep = (idx) => {
    setSequence((s) => ({ ...s, steps: s.steps.filter((_, i) => i !== idx).map((st, i) => ({ ...st, order: i })) }));
  };

  const toggleDay = (i) => {
    setSequence((s) => {
      const set = new Set(s.sending.weekdays);
      if (set.has(i)) set.delete(i); else set.add(i);
      return { ...s, sending: { ...s.sending, weekdays: Array.from(set).sort() } };
    });
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error("Enter an email to test");
    setSending(true);
    try {
      const r = await api.post("/email/test-send", { to_email: testEmail, subject: testSubject, body: testBody });
      toast.success(`Test sent via ${r.data.provider} (${r.data.mode})`, { description: `Message id: ${r.data.message_id}` });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setSending(false);
    }
  };

  const canGoNext = useMemo(() => {
    if (step === 1) return isConnected;
    if (step === 2) return !!sequence.steps.length;
    if (step === 3) return sequence.name.trim().length > 0 && sequence.steps.every((s) => s.subject && s.body);
    if (step === 4) return sequence.sending.window_start_hour < sequence.sending.window_end_hour && sequence.sending.weekdays.length > 0;
    return true;
  }, [step, isConnected, sequence]);

  const activate = async () => {
    setSaving(true);
    try {
      const r = await api.post("/email/sequences", sequence);
      toast.success("Sequence activated", { description: sequence.auto_enroll_new_contacts ? "New contacts will be auto-enrolled." : "Enroll contacts from the automation dashboard." });
      nav(`/dashboard/email-automation?sequence=${r.data.sequence_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save sequence");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="email-setup-page">
      <div className="mb-8">
        <div className="overline mb-2">// email automation</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Follow-up wizard</h1>
        <p className="mt-3 text-sm md:text-base" style={{ color: "var(--text-2)" }}>
          Five steps to turn every new contact into an automatic warm-up sequence.
        </p>
      </div>

      <ProgressRail current={step} />

      <div className="mt-10 border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        {step === 1 && (
          <StepConnect
            loading={connLoading}
            isConnected={isConnected}
            isDevMode={isDevMode}
            config={config}
            connection={connection}
            onConnect={connectGoogle}
            onDisconnect={disconnect}
          />
        )}
        {step === 2 && (
          <StepTemplate
            templates={starterTemplates}
            selectedId={selectedTemplateId}
            onPick={pickTemplate}
          />
        )}
        {step === 3 && (
          <StepPersonalize
            sequence={sequence}
            setSequence={setSequence}
            updateStep={updateStep}
            addStep={addStep}
            removeStep={removeStep}
          />
        )}
        {step === 4 && (
          <StepSchedule sequence={sequence} setSequence={setSequence} toggleDay={toggleDay} />
        )}
        {step === 5 && (
          <StepReview
            sequence={sequence}
            connection={connection}
            isDevMode={isDevMode}
            testEmail={testEmail}
            setTestEmail={setTestEmail}
            testSubject={testSubject}
            setTestSubject={setTestSubject}
            testBody={testBody}
            setTestBody={setTestBody}
            sendTest={sendTest}
            sending={sending}
            onActivate={activate}
            saving={saving}
            userEmail={user?.email}
          />
        )}
      </div>

      <div className="flex justify-between mt-8">
        <button
          data-testid="wizard-back-btn"
          className="btn-secondary flex items-center gap-2"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
        >
          <ArrowLeft weight="bold" /> Back
        </button>
        {step < 5 ? (
          <button
            data-testid="wizard-next-btn"
            className="btn-primary flex items-center gap-2"
            onClick={() => setStep((s) => Math.min(5, s + 1))}
            disabled={!canGoNext}
          >
            Next <ArrowRight weight="bold" />
          </button>
        ) : (
          <button
            data-testid="wizard-activate-btn"
            className="btn-primary flex items-center gap-2"
            onClick={activate}
            disabled={saving || !canGoNext}
            style={{ background: "var(--accent)" }}
          >
            {saving ? "Activating..." : "Activate sequence"} <CheckCircle weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

function ProgressRail({ current }) {
  return (
    <div className="grid grid-cols-5 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
      {STEPS.map((s) => {
        const isActive = s.id === current;
        const done = s.id < current;
        return (
          <div
            key={s.id}
            data-testid={`wizard-step-indicator-${s.id}`}
            className="border-r border-b p-4"
            style={{
              borderColor: "var(--border)",
              background: isActive ? "var(--surface-2)" : "transparent",
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-6 flex items-center justify-center font-mono text-xs font-bold"
                style={{
                  background: done ? "var(--accent)" : isActive ? "var(--accent)" : "transparent",
                  color: done || isActive ? "var(--bg)" : "var(--text-3)",
                  border: `1px solid ${done || isActive ? "var(--accent)" : "var(--border)"}`,
                }}
              >
                {done ? "✓" : s.id}
              </div>
              <div className="overline" style={{ color: isActive || done ? "var(--text)" : "var(--text-3)" }}>{s.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------- Step 1 -------------------------------- */
function StepConnect({ loading, isConnected, isDevMode, config, connection, onConnect, onDisconnect }) {
  return (
    <div className="p-6 md:p-10">
      <div className="overline mb-2">// step 1</div>
      <h2 className="font-display font-black text-3xl tracking-tight mb-2">Connect the inbox that will send your follow-ups</h2>
      <p className="text-sm mb-8" style={{ color: "var(--text-2)" }}>
        MyNexusCard never stores your Gmail password. We use OAuth via Nylas — you can revoke access anytime.
      </p>

      {loading ? (
        <div className="text-sm" style={{ color: "var(--text-3)" }}>Checking connection...</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Google */}
          <div className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="connect-google-card">
            <div className="flex items-center gap-3 mb-3">
              <GoogleLogo size={28} weight="bold" style={{ color: "#EA4335" }} />
              <div>
                <div className="font-display font-bold text-xl">Google (Gmail)</div>
                <div className="overline">recommended</div>
              </div>
            </div>
            {isConnected && (
              <div className="mb-4 p-3 border" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.05)" }} data-testid="connected-badge">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle weight="fill" style={{ color: "var(--accent)" }} />
                  <span className="font-semibold">
                    {isDevMode ? "Demo Mode active" : "Connected"}
                  </span>
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>
                  {connection?.email}
                </div>
                {isDevMode && (
                  <div className="text-xs mt-2" style={{ color: "var(--text-3)" }}>
                    Messages send from a MyNexusCard-owned demo sender until a real Nylas connection is added.
                  </div>
                )}
                {config?.branded_sender && (
                  <div className="text-xs mt-2" style={{ color: "var(--text-3)" }} data-testid="branded-sender-note">
                    {connection?.note}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              {!isConnected && (
                <button
                  className="btn-primary flex items-center gap-2"
                  onClick={onConnect}
                  data-testid="connect-google-btn"
                >
                  <PlugsConnected weight="bold" /> {isDevMode ? "Enable Demo Mode" : "Connect Gmail"}
                </button>
              )}
              {isConnected && !config?.branded_sender && (
                <>
                  <button className="btn-secondary" onClick={onConnect} data-testid="reconnect-google-btn">Reconnect</button>
                  <button className="btn-secondary" onClick={onDisconnect} data-testid="disconnect-btn">Disconnect</button>
                </>
              )}
            </div>
          </div>

          {/* Microsoft (Outlook) */}
          <div className="border p-6 relative" style={{ borderColor: "var(--border)" }} data-testid="connect-microsoft-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-7 h-7 grid grid-cols-2 gap-0.5">
                <div style={{ background: "#F25022" }} />
                <div style={{ background: "#7FBA00" }} />
                <div style={{ background: "#00A4EF" }} />
                <div style={{ background: "#FFB900" }} />
              </div>
              <div>
                <div className="font-display font-bold text-xl">Microsoft (Outlook)</div>
                <div className="overline">365 / outlook.com</div>
              </div>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--text-3)" }}>
              Same OAuth flow as Gmail — password never stored. Outlook connection is coming soon.
            </p>
            <button
              className="btn-secondary flex items-center gap-2"
              data-testid="connect-microsoft-btn"
              onClick={async () => {
                try {
                  const r = await api.get("/email/connect/microsoft");
                  if (r.data.mode === "dev") {
                    toast.message("Demo Mode", { description: r.data.message });
                  } else if (r.data.auth_url) {
                    window.location.href = r.data.auth_url;
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not start OAuth");
                }
              }}
            >
              <PlugsConnected weight="bold" /> Connect Outlook
            </button>
          </div>
        </div>
      )}

      {isDevMode && (
        <div className="mt-6 p-4 border flex gap-3 text-sm" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.06)" }} data-testid="dev-mode-notice">
          <Warning weight="fill" style={{ color: "var(--accent)" }} />
          <div>
            <div className="font-semibold">Demo Mode is enabled</div>
            <div style={{ color: "var(--text-2)" }}>
              Environment flag <code>EMAIL_DEV_MODE=true</code>. All outgoing messages are sent from a MyNexusCard-owned demo sender and prefixed with <b>[DEMO]</b>. Nothing is sent from your Gmail account until real Nylas credentials are configured.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- Step 2 -------------------------------- */
function StepTemplate({ templates, selectedId, onPick }) {
  return (
    <div className="p-6 md:p-10">
      <div className="overline mb-2">// step 2</div>
      <h2 className="font-display font-black text-3xl tracking-tight mb-2">Start from a template</h2>
      <p className="text-sm mb-8" style={{ color: "var(--text-2)" }}>Every template is fully editable in the next step.</p>
      <div className="grid md:grid-cols-2 gap-6">
        {templates.map((t) => (
          <button
            key={t.id}
            onClick={() => onPick(t)}
            className="text-left border p-6 hover:bg-[#0F1E24]"
            data-testid={`template-${t.id}`}
            style={{
              borderColor: selectedId === t.id ? "var(--accent)" : "var(--border)",
              background: selectedId === t.id ? "rgba(45,212,212,0.05)" : "transparent",
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <EnvelopeSimple weight="bold" style={{ color: "var(--accent)" }} />
              <div className="font-display font-bold text-xl">{t.name}</div>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>{t.description}</p>
            <div className="overline mb-3">{t.steps.length} steps</div>
            <ol className="text-xs space-y-1" style={{ color: "var(--text-3)" }}>
              {t.steps.map((s, i) => (
                <li key={i}>Day {t.steps.slice(0, i + 1).reduce((a, x) => a + x.delay_days, 0)} — <span style={{ color: "var(--text-2)" }}>{s.subject}</span></li>
              ))}
            </ol>
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------- Step 3 -------------------------------- */
function StepPersonalize({ sequence, setSequence, updateStep, addStep, removeStep }) {
  return (
    <div className="p-6 md:p-10">
      <div className="overline mb-2">// step 3</div>
      <h2 className="font-display font-black text-3xl tracking-tight mb-2">Make it sound like you</h2>
      <p className="text-sm mb-8" style={{ color: "var(--text-2)" }}>
        Available merge tags: <code>{"{{contact.first_name}}"}</code>, <code>{"{{contact.company}}"}</code>, <code>{"{{me.first_name}}"}</code>, <code>{"{{me.calendly_url}}"}</code>, <code>{"{{me.card_url}}"}</code>, <code>{"{{event.name}}"}</code>.
      </p>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="label-base">Sequence name</label>
          <input
            className="input-base"
            value={sequence.name}
            onChange={(e) => setSequence((s) => ({ ...s, name: e.target.value }))}
            placeholder="e.g. Post-event follow-up"
            data-testid="sequence-name-input"
          />
        </div>
        <div>
          <label className="label-base">Internal note (optional)</label>
          <input
            className="input-base"
            value={sequence.description}
            onChange={(e) => setSequence((s) => ({ ...s, description: e.target.value }))}
            placeholder="Only visible to you"
            data-testid="sequence-desc-input"
          />
        </div>
      </div>

      <div className="space-y-6">
        {sequence.steps.map((st, i) => (
          <div key={i} className="border p-5" style={{ borderColor: "var(--border)" }} data-testid={`step-editor-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-lg">Step {i + 1}</div>
              <button
                onClick={() => removeStep(i)}
                className="text-xs flex items-center gap-1 hover:text-white"
                style={{ color: "var(--text-3)" }}
                data-testid={`remove-step-${i}`}
              >
                <Trash weight="bold" /> Remove
              </button>
            </div>
            <div className="grid md:grid-cols-4 gap-4 mb-3">
              <div>
                <label className="label-base">Send after (days)</label>
                <input
                  type="number"
                  min="0"
                  max="180"
                  className="input-base"
                  value={st.delay_days}
                  onChange={(e) => updateStep(i, { delay_days: Number(e.target.value) })}
                  data-testid={`step-delay-${i}`}
                />
              </div>
              <div className="md:col-span-3">
                <label className="label-base">Subject</label>
                <input
                  className="input-base"
                  value={st.subject}
                  onChange={(e) => updateStep(i, { subject: e.target.value })}
                  data-testid={`step-subject-${i}`}
                />
              </div>
            </div>
            <label className="label-base">Body</label>
            <textarea
              className="input-base"
              style={{ minHeight: 120, fontFamily: "JetBrains Mono, monospace" }}
              value={st.body}
              onChange={(e) => updateStep(i, { body: e.target.value })}
              data-testid={`step-body-${i}`}
            />
          </div>
        ))}
        <button className="btn-secondary flex items-center gap-2" onClick={addStep} data-testid="add-step-btn">
          <Plus weight="bold" /> Add another step
        </button>
      </div>
    </div>
  );
}

/* -------------------------------- Step 4 -------------------------------- */
function StepSchedule({ sequence, setSequence, toggleDay }) {
  return (
    <div className="p-6 md:p-10">
      <div className="overline mb-2">// step 4</div>
      <h2 className="font-display font-black text-3xl tracking-tight mb-2">When should this send?</h2>
      <p className="text-sm mb-8" style={{ color: "var(--text-2)" }}>Emails only fire inside your allowed window.</p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border p-5" style={{ borderColor: "var(--border)" }}>
          <div className="overline mb-4"><Clock weight="bold" style={{ display: "inline", marginRight: 6 }} /> Sending hours (local)</div>
          <div className="flex items-center gap-3">
            <input
              type="number" min="0" max="23"
              className="input-base"
              style={{ width: 80 }}
              value={sequence.sending.window_start_hour}
              onChange={(e) => setSequence((s) => ({ ...s, sending: { ...s.sending, window_start_hour: Number(e.target.value) } }))}
              data-testid="sending-start-hour"
            />
            <span className="text-sm" style={{ color: "var(--text-3)" }}>to</span>
            <input
              type="number" min="1" max="24"
              className="input-base"
              style={{ width: 80 }}
              value={sequence.sending.window_end_hour}
              onChange={(e) => setSequence((s) => ({ ...s, sending: { ...s.sending, window_end_hour: Number(e.target.value) } }))}
              data-testid="sending-end-hour"
            />
            <span className="text-sm" style={{ color: "var(--text-3)" }}>o&apos;clock</span>
          </div>
          <div className="overline mt-6 mb-2">Timezone offset</div>
          <input
            type="number"
            className="input-base"
            style={{ width: 120 }}
            value={sequence.sending.tz_offset_hours}
            onChange={(e) => setSequence((s) => ({ ...s, sending: { ...s.sending, tz_offset_hours: Number(e.target.value) } }))}
            data-testid="sending-tz-offset"
          />
          <div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>Hours from UTC. Currently detected: {(-new Date().getTimezoneOffset() / 60).toFixed(1)}</div>
        </div>

        <div className="border p-5" style={{ borderColor: "var(--border)" }}>
          <div className="overline mb-4">Allowed weekdays</div>
          <div className="flex gap-2 flex-wrap">
            {WEEKDAY_LABELS.map((lbl, i) => {
              const on = sequence.sending.weekdays.includes(i);
              return (
                <button
                  key={i}
                  onClick={() => toggleDay(i)}
                  className="px-3 py-2 text-xs font-mono border"
                  style={{
                    borderColor: on ? "var(--accent)" : "var(--border)",
                    background: on ? "var(--accent)" : "transparent",
                    color: on ? "var(--bg)" : "var(--text-2)",
                  }}
                  data-testid={`weekday-${i}`}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex items-start gap-3">
            <label htmlFor="auto-enroll" className="flex items-start gap-3 cursor-pointer text-sm" style={{ color: "var(--text-2)" }}>
              <input
                type="checkbox"
                checked={!!sequence.auto_enroll_new_contacts}
                onChange={(e) => setSequence((s) => ({ ...s, auto_enroll_new_contacts: e.target.checked }))}
                data-testid="auto-enroll-toggle"
                id="auto-enroll"
                style={{ marginTop: 4 }}
              />
              <span>
                <b style={{ color: "var(--text)" }}>Auto-enroll new contacts</b>
                <div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>
                  Every contact created from a scan, referral, shared card, or CSV import will be enrolled in this sequence automatically. Only one sequence can be the auto-enroll target at a time.
                </div>
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Step 5 -------------------------------- */
function StepReview({ sequence, connection, isDevMode, testEmail, setTestEmail, testSubject, setTestSubject, testBody, setTestBody, sendTest, sending, userEmail }) {
  return (
    <div className="p-6 md:p-10">
      <div className="overline mb-2">// step 5</div>
      <h2 className="font-display font-black text-3xl tracking-tight mb-2">Review & activate</h2>
      <p className="text-sm mb-8" style={{ color: "var(--text-2)" }}>Send yourself a test, then flip it on.</p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border p-5" style={{ borderColor: "var(--border)" }}>
          <div className="overline mb-3">summary</div>
          <div className="text-sm space-y-2">
            <div><span style={{ color: "var(--text-3)" }}>Sending from</span> <b>{connection?.email || "—"}</b> {isDevMode && <span className="tag ml-2" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>DEMO</span>}</div>
            <div><span style={{ color: "var(--text-3)" }}>Sequence</span> <b>{sequence.name || "—"}</b></div>
            <div><span style={{ color: "var(--text-3)" }}>Steps</span> <b>{sequence.steps.length}</b></div>
            <div><span style={{ color: "var(--text-3)" }}>Window</span> <b>{sequence.sending.window_start_hour}:00 – {sequence.sending.window_end_hour}:00</b></div>
            <div><span style={{ color: "var(--text-3)" }}>Days</span> <b>{sequence.sending.weekdays.map((i) => WEEKDAY_LABELS[i]).join(", ")}</b></div>
            <div><span style={{ color: "var(--text-3)" }}>Auto-enroll</span> <b>{sequence.auto_enroll_new_contacts ? "yes — new contacts" : "no — manual"}</b></div>
          </div>

          <div className="overline mt-6 mb-3">steps</div>
          <ol className="text-sm space-y-2">
            {sequence.steps.map((st, i) => (
              <li key={i} className="border p-3" style={{ borderColor: "var(--border)" }}>
                <div className="text-xs" style={{ color: "var(--text-3)" }}>Day {sequence.steps.slice(0, i + 1).reduce((a, x) => a + Number(x.delay_days || 0), 0)} · Step {i + 1}</div>
                <div className="font-semibold mt-1">{st.subject}</div>
                <div className="text-xs mt-1 line-clamp-2" style={{ color: "var(--text-2)" }}>{st.body}</div>
              </li>
            ))}
          </ol>
        </div>

        <div className="border p-5" style={{ borderColor: "var(--border)" }}>
          <div className="overline mb-3">send a test</div>
          <label className="label-base">To (your own address)</label>
          <input
            className="input-base"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder={userEmail || "you@company.com"}
            data-testid="test-email-input"
          />
          <label className="label-base mt-4">Subject</label>
          <input
            className="input-base"
            value={testSubject}
            onChange={(e) => setTestSubject(e.target.value)}
            data-testid="test-subject-input"
          />
          <label className="label-base mt-4">Body</label>
          <textarea
            className="input-base"
            style={{ minHeight: 100, fontFamily: "JetBrains Mono, monospace" }}
            value={testBody}
            onChange={(e) => setTestBody(e.target.value)}
            data-testid="test-body-input"
          />
          <div className="mt-4">
            <button className="btn-secondary flex items-center gap-2" onClick={sendTest} disabled={sending} data-testid="send-test-btn">
              <PaperPlaneTilt weight="bold" /> {sending ? "Sending..." : "Send test"}
            </button>
          </div>
          {isDevMode && (
            <div className="mt-4 text-xs p-3 border" style={{ borderColor: "var(--border)", color: "var(--text-3)" }}>
              In Demo Mode, Resend restricts recipients to your own verified email. Real Nylas mode removes that limit.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
